// src/services/weatherApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  OMNISTACK_BASE_URL, 
  getPrepareHeaders 
} from '@/hooks/common/common-functions'

export const weatherApi = createApi({
  reducerPath: 'weatherApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}business-weather`,
    prepareHeaders: (headers) => {
      // Add standard headers
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });
      
      // Add business-specific API key
      const businessApiKey = localStorage.getItem('businessApiKey');
      if (businessApiKey) {
        headers.set('business-x-api-key', businessApiKey);
      }
      
      return headers;
    },
  }),
  tagTypes: ['WeatherSettings', 'WeatherForecast', 'WeatherAlerts', 'ProjectAlerts', 'ProjectsWithSites', 'ProjectSettings'],
  endpoints: (builder) => ({
    // Get weather settings for a business
    getWeatherSettings: builder.query({
      query: (businessId) => `/settings/${businessId}`,
      providesTags: ['WeatherSettings'],
    }),
    
    // Update weather settings for a business
    updateWeatherSettings: builder.mutation({
      query: ({ businessId, settings }) => ({
        url: `/settings/${businessId}`,
        method: 'PUT',
        body: settings,
      }),
      invalidatesTags: ['WeatherSettings'],
    }),
    
    // Get current weather for a location
    getCurrentWeather: builder.query({
      query: ({ businessId, latitude, longitude }) => ({
        url: `/current-weather/${businessId}`,
        params: { latitude, longitude },
      }),
    }),
    
    // Get weather forecast for business projects
    getProjectsForecasts: builder.query({
      query: (businessId) => `/forecast/${businessId}`,
      providesTags: ['WeatherForecast'],
    }),
    
    // Get weather forecast for a specific project
    getProjectForecast: builder.query({
      query: ({ businessId, projectId }) => ({
        url: `/forecast/${businessId}`,
        params: { projectId },
      }),
      providesTags: (_, __, { projectId }) => 
        [{ type: 'WeatherForecast', id: projectId }],
    }),
    
   // Get all active weather alerts for a business
getActiveAlerts: builder.query({
    query: (params) => {
      // Handle different parameter formats
      if (typeof params === 'string') {
        // If just businessId is passed as a string
        return `/alerts/${params}`;
      }
      
      // If params is an object with businessId and other options
      const { businessId, projectId, includeResolved } = params;
      const queryParams: Record<string, any> = {};
      
      // Add optional parameters
      if (projectId) queryParams.projectId = projectId;
      if (includeResolved !== undefined) queryParams.includeResolved = includeResolved;
      
      return {
        url: `/alerts/${businessId}`,
        params: queryParams
      };
    },
    providesTags: ['WeatherAlerts'],
  }),
  
    // Get active weather alerts for a specific project
    getProjectAlerts: builder.query({
      query: ({ businessId, projectId }) => ({
        url: `/alerts/${businessId}`,
        params: { projectId },
      }),
      providesTags: ['ProjectAlerts'],
    }),
    
    // Get project-specific settings
    getProjectSettings: builder.query({
      query: ({ businessId, projectId }) => ({
        url: `/project-settings/${businessId}/project/${projectId}`,
      }),
      providesTags: (_, __, { projectId }) => [{ type: 'ProjectSettings', id: projectId }],
    }),
    
    // Configure project-specific weather alerts
    configureProjectAlerts: builder.mutation({
      query: ({ businessId, projectId, config }) => ({
        url: `/alerts/${businessId}/project/${projectId}`,
        method: 'POST',
        body: config,
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'ProjectAlerts', id: projectId },
        { type: 'ProjectSettings', id: projectId },
        'WeatherAlerts'
      ],
    }),
    
    // Disable weather alerts for a project
    disableProjectAlerts: builder.mutation({
      query: ({ businessId, projectId }) => ({
        url: `/alerts/${businessId}/project/${projectId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'ProjectAlerts', id: projectId },
        { type: 'ProjectSettings', id: projectId },
        'WeatherAlerts'
      ],
    }),
    
    getProjectsWithSites: builder.query({
        query: (businessId) => `/projects-with-sites/${businessId}`,
        providesTags: ['ProjectsWithSites'],
      }),

    // Resolve weather alert
    resolveAlert: builder.mutation({
        query: ({ businessId, alertId }) => ({
            url: `/alerts/${businessId}/resolve/${alertId}`,
            method: 'PUT',
        }),
        invalidatesTags: ['WeatherAlerts', 'ProjectAlerts'],
    }),

    // Manually trigger weather check for all projects
    triggerWeatherCheck: builder.mutation({
      query: (businessId) => ({
        url: `/trigger-check/${businessId}`,
        method: 'POST',
      }),
      invalidatesTags: ['WeatherAlerts', 'ProjectAlerts', 'WeatherForecast'],
    }),
  }),
})

export const {
  useGetWeatherSettingsQuery,
  useUpdateWeatherSettingsMutation,
  useGetCurrentWeatherQuery,
  useGetProjectsForecastsQuery,
  useGetProjectForecastQuery,
  useGetActiveAlertsQuery,
  useGetProjectAlertsQuery,
  useGetProjectSettingsQuery,
  useConfigureProjectAlertsMutation,
  useDisableProjectAlertsMutation,
  useTriggerWeatherCheckMutation,
  useGetProjectsWithSitesQuery,
  useResolveAlertMutation,
} = weatherApi